/**
 * A sandboxed preview of a rendered email — the same safe iframe pattern as
 * EmailHtmlModal (no scripts, no same-origin; images + fonts still load). The
 * `html` is a full email document.
 */

import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

export interface EmailPreviewModalProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly html: string;
  readonly onClose: () => void;
}

export function EmailPreviewModal({
  title,
  subtitle,
  html,
  onClose,
}: EmailPreviewModalProps): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onBackdrop = useCallback(
    (e: React.MouseEvent): void => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={onBackdrop}>
      <div
        className="modal-card email-modal-card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <h3 className="modal-title">{title}</h3>
        {subtitle !== undefined ? (
          <p className="modal-subtitle">{subtitle}</p>
        ) : null}
        <iframe
          className="email-modal-frame"
          title={title}
          sandbox=""
          srcDoc={html}
        />
        <div className="modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
